import {getAuthCallbackUrlError} from './authCallbackErrors.mjs';
import {readSupportHandoff} from './supportPresentation.mjs';
// Presentation order is the Founder's contract; IDs are Supabase provider IDs.
export const SOCIAL_PROVIDERS = Object.freeze([
  {id:'linkedin_oidc', name:'LinkedIn', icon:'/icons/linkedin.svg', flag:'VITE_ENABLE_LINKEDIN_AUTH'},
  {id:'facebook', name:'Facebook', icon:'/assets/images/facebook.svg', flag:'VITE_ENABLE_FACEBOOK_AUTH'},
  {id:'google', name:'Google', icon:'/assets/images/google.svg', flag:'VITE_ENABLE_GOOGLE_AUTH'},
]);
export const isSocialProvider = value => SOCIAL_PROVIDERS.some(p => p.id === value);
export function safeAuthDestination(value) {
  const path=String(value||'').trim();
  if(!path.startsWith('/') || path.startsWith('//') || /[\\\x00-\x1f]/.test(path))return '';
  try { if(new URL(path,'https://app.afrimed.tech').origin!=='https://app.afrimed.tech')return ''; }catch{return '';}
  return path;
}
// Direct callback/login intent wins; existing Help deep links remain supported.
export function authJourneyLanguage(search = '') {
  const language = new URLSearchParams(search).get('lang');
  return ['en-GB', 'fr'].includes(language) ? language : readSupportHandoff(search, '/login').language || '';
}
export function socialCallbackPath({provider,language,next,member}={}) {
  if(!isSocialProvider(provider))throw Error('unsupported_provider');
  const q=new URLSearchParams({provider,lang:String(language).startsWith('fr')?'fr':'en-GB'});
  const dest=safeAuthDestination(next);if(dest)q.set('next',dest);
  if(member && /^[a-zA-Z0-9_-]{1,100}$/.test(member))q.set('member',member);
  return `/auth/callback?${q}`;
}
export function socialFailurePath(url,error) {
  const u=new URL(url,'https://app.afrimed.tech');
  const detail=String(error?.message||error?.errorDescription||'').toLowerCase();
  const code=String(error?.error||error?.errorCode||error?.code||'');
  const reason=detail.includes('email')?'email':code==='access_denied'||code==='user_cancelled_login'||detail.includes('cancel')||detail.includes('denied')?'cancelled':'failed';
  const q=new URLSearchParams({oauth_error:reason,view:'credentials',lang:u.searchParams.get('lang')==='fr'?'fr':'en-GB'});
  const next=safeAuthDestination(u.searchParams.get('next'));if(next)q.set('next',next);
  const member=u.searchParams.get('member');if(member&&/^[a-zA-Z0-9_-]{1,100}$/.test(member))q.set('member',member);
  return `/login?${q}`;
}
// Some provider cancellations reach the app origin without the redirectTo query.
// Keep only short-lived, tab-local presentation intent. This never grants access,
// restores a session, or substitutes for Supabase's OAuth state validation.
const SOCIAL_RETURN_KEY = 'afrimed:social-return:v1';
const SOCIAL_RETURN_TTL = 15 * 60 * 1000;
function tabStorage() {
  try { return typeof window === 'undefined' ? null : window.sessionStorage; } catch { return null; }
}
export function clearSocialReturnIntent(storage = tabStorage()) {
  try { storage?.removeItem(SOCIAL_RETURN_KEY); } catch {}
}
function saveSocialReturnIntent(provider, redirectTo, storage, now) {
  clearSocialReturnIntent(storage);
  try {
    const u = new URL(redirectTo);
    if (u.pathname !== '/auth/callback' || u.searchParams.get('provider') !== provider) return;
    const path = socialCallbackPath({provider, language:u.searchParams.get('lang'), next:u.searchParams.get('next'), member:u.searchParams.get('member')});
    storage?.setItem(SOCIAL_RETURN_KEY, JSON.stringify({path, at:now}));
  } catch {}
}
export function socialFallbackFailurePath(value, storage = tabStorage(), now = Date.now()) {
  try {
    const url = new URL(value);
    if (!['/', '/login'].includes(url.pathname)) return '';
    const error = getAuthCallbackUrlError(url);
    if (!error) return '';
    const pending = JSON.parse(storage?.getItem(SOCIAL_RETURN_KEY) || 'null');
    if (!pending || !Number.isFinite(pending.at) || now < pending.at || now - pending.at > SOCIAL_RETURN_TTL) return '';
    const path = safeAuthDestination(pending.path);
    const intent = new URL(path || '/', url.origin);
    const provider = intent.searchParams.get('provider');
    if (intent.pathname !== '/auth/callback' || !isSocialProvider(provider)) return '';
    // Rebuild rather than trusting other stored query fields or auth payloads.
    return socialFailurePath(socialCallbackPath({provider, language:intent.searchParams.get('lang'), next:intent.searchParams.get('next'), member:intent.searchParams.get('member')}), error);
  } catch { return ''; }
}
// One OAuth start per explicit user action, even before React updates disabled.
export function createSocialAuthStarter(auth, {storage = tabStorage(), now = Date.now} = {}) {
  let busy=false;
  return {reset(){busy=false;clearSocialReturnIntent(storage);},async start(provider,redirectTo){
    if(busy)return {ignored:true};
    if(!isSocialProvider(provider))throw Error('unsupported_provider');
    busy=true;
    try {
      saveSocialReturnIntent(provider, redirectTo, storage, now());
      const options={redirectTo};
      if(provider==='linkedin_oidc')options.scopes='openid profile email';
      // No prompt=login, auth_type=reauthenticate, posting scope or second OTP.
      const result=await auth.signInWithOAuth({provider,options});
      if(result.error)throw result.error;
      return result;
    }catch(error){busy=false;clearSocialReturnIntent(storage);throw error;}
  }};
}
export function socialAuthNotice(code,fr=false) {
  if(code==='cancelled')return fr?'Connexion annulée. Vous pouvez réessayer ou utiliser votre connexion habituelle.':'Sign-in cancelled. Try again or use your usual sign-in method.';
  if(code==='email')return fr?'Le fournisseur n’a pas communiqué d’adresse e-mail vérifiée. Utilisez votre connexion habituelle ou le parcours vérifié par e-mail ; ne créez pas de compte en double.':'The provider did not supply a verified email. Use your usual sign-in method or the verified email journey; do not create a duplicate account.';
  return code?(fr?'La connexion n’a pas abouti. Réessayez ou utilisez votre connexion habituelle.':'Sign-in did not complete. Try again or use your usual sign-in method.'):'';
}

// A provider may authenticate without supplying email. Do not infer an address,
// assign a category, or replace the existing identity in that case.
export async function requireSocialVerifiedEmail(auth, user) {
  if (String(user?.email || '').trim() && user?.email_confirmed_at) return;
  try { await auth.signOut({scope:'local'}); } catch { /* Login error route never auto-forwards this session. */ }
  throw new Error('Provider verified email required');
}
